// ============================================
// B356 Fase 1 (AI_154) — CLIENTE DA API DO SNCR (navegador)
// ============================================
// Fluxo oficial (Manual da API v5 §2.2; Instruções de integração v1.0 §4),
// todo no NAVEGADOR e a partir de um domínio .br:
//   1. redireciona para  {base}auth/login?client_url=<esta página>
//   2. o gov.br autentica o médico e volta com ?session_id=… (uso único, 30 s,
//      vinculado ao Origin que iniciou o login)
//   3. GET {base}auth/token?session_id=…  → access_token (30 s, sem refresh)
//   4. na MESMA hora, POST numeracoes/… com Authorization: Bearer
//
// Por que não passar pelo servidor (api/*): o session_id é amarrado ao Origin
// do navegador que iniciou o login; o token vive 30 s. Intermediar só criaria
// um ponto a mais de falha. O token nunca é guardado (nem em sessionStorage):
// é usado e descartado dentro da mesma função.

import { baseApiSncr, AmbienteSncr } from "./configSncr";
import { TipoNumeracao } from "./numeracaoSncr";

export interface DadosPrescritorSncr {
  /** "CRM" | "CRO" | "CRMV" */
  conselho: string;
  /** número de inscrição, só dígitos */
  documento: string;
  uf: string;
}

export interface BlocoRecebido {
  inicio: string;
  fim: string;
  quantidade: number;
  mensagem?: string;
}

export class ErroSncr extends Error {
  status: number;
  constructor(mensagem: string, status: number) {
    super(mensagem);
    this.status = status;
  }
}

/** URL para onde o navegador vai para iniciar o login gov.br. */
export function urlLoginSncr(clientUrl: string, amb?: AmbienteSncr, state?: string): string {
  const base = `${baseApiSncr(amb)}auth/login?client_url=${encodeURIComponent(clientUrl)}`;
  return state ? `${base}&state=${encodeURIComponent(state)}` : base;
}

/** Lê a mensagem de erro nos formatos que a API usa. */
async function mensagemDeErro(resp: Response): Promise<string> {
  try {
    const corpo = await resp.json();
    return (
      corpo?.mensagem ||
      corpo?.error ||
      corpo?.errorObject?.description ||
      corpo?.message ||
      `HTTP ${resp.status}`
    );
  } catch {
    return `HTTP ${resp.status}`;
  }
}

/** Troca o session_id (uso único) pelo access_token. */
export async function trocarSessaoPorToken(
  sessionId: string,
  amb?: AmbienteSncr,
  f: typeof fetch = fetch
): Promise<string> {
  // Validação ao vivo (AI_154): "Sessão inválida ou expirada" na 1ª tentativa.
  // Igual ao exemplo OFICIAL (Instruções de integração v1.0 §4.4): GET simples,
  // SEM cabeçalho Content-Type — sem ele o navegador não faz a consulta prévia
  // (OPTIONS) de CORS, e o código de uso único vai ao servidor numa só chamada.
  const resp = await f(
    `${baseApiSncr(amb)}auth/token?session_id=${encodeURIComponent(sessionId)}`,
    { method: "GET" }
  );
  if (!resp.ok) {
    throw new ErroSncr(
      `${await mensagemDeErro(resp)} [HTTP ${resp.status}]`,
      resp.status
    );
  }
  const data = await resp.json();
  if (!data?.access_token) throw new ErroSncr("Resposta sem access_token.", 500);
  return data.access_token as string;
}

/**
 * Pede um bloco de 1.000 números de RCE ou RET (Manual v5 §2.3.2).
 * Limites: 3 pedidos/mês por inscrição; 3.000 números/mês por tipo.
 */
export async function solicitarBlocoRceRet(
  token: string,
  tipo: TipoNumeracao,
  prescritor: DadosPrescritorSncr,
  cnpj: string,
  amb?: AmbienteSncr,
  f: typeof fetch = fetch
): Promise<BlocoRecebido> {
  const resp = await f(
    `${baseApiSncr(amb)}numeracoes/receita-especial-retencao`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        conselho: prescritor.conselho,
        tipo,
        documento: prescritor.documento,
        uf: prescritor.uf,
        cnpj,
      }),
    }
  );
  if (resp.status !== 201 && resp.status !== 200) {
    throw new ErroSncr(await mensagemDeErro(resp), resp.status);
  }
  const data = await resp.json();
  return {
    inicio: String(data?.inicio ?? ""),
    fim: String(data?.fim ?? ""),
    quantidade: Number(data?.quantidade ?? 0),
    mensagem: data?.mensagem,
  };
}

/** Normaliza o registro profissional do cadastro para o formato da API. */
export function prescritorDoCadastro(reg?: {
  tipo?: string;
  numero?: string;
  uf?: string;
} | null): DadosPrescritorSncr | null {
  if (!reg?.numero || !reg?.uf) return null;
  const conselho = (reg.tipo || "CRM").toUpperCase();
  if (!["CRM", "CRO", "CRMV"].includes(conselho)) return null;
  const documento = String(reg.numero).replace(/\D/g, "");
  if (!documento) return null;
  return { conselho, documento, uf: String(reg.uf).toUpperCase() };
}
